import {
  defaultDiscount,
  DiscountCodeType,
  PricingPlan,
  TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT,
  type TAgeViewState,
  type TCatFoodTypeBefore,
  type TDiscount,
  type THealthConcernsToAddress,
  type TMotivationsForSwitch,
  type TPricingPlan,
} from "@./suf-store";

import { Theme } from "apps/website/maps/Theme.map";

import {
  FlowNavigationContext,
  FlowPage,
  PageRequirement,
} from "../../shared/constants";

const LIGHT_GREY = "light-grey";

export const defaultAgeViewState: TAgeViewState = {
  showBirthdaySection: false,
  showNeuteredSection: false,
};

// Sign-up flow store TTL: local store gets cleared after this many days of inactivity
export const SIGNUP_FLOW_TTL_DAYS = 30;

export const trialWeekDefaultDiscountCode = "FIXED_PRICE_SINGLE_10";

export const trialWeekDefaultDiscount = {
  discountCode: TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT.discountCode,
  type: DiscountCodeType.ABSOLUTE_TOTAL,
  value: TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT.value,
};

export const defaultDiscountByTrialPlan: Partial<Record<TPricingPlan, TDiscount>> = {
  [PricingPlan.TRIAL]: defaultDiscount,
  [PricingPlan.TRIAL_WEEK]: trialWeekDefaultDiscount,
};


export const defaultViewState = {
  checkout: {
    // Delivery/Shipping Address Section
    showManualEntryButton: true,
    showAddressSelect: false,
    showShippingAddressFields: false,
    hasClickedFindAddress: false,
    isDeliveryComplete: false,
    hasCompletedDeliveryOnce: false,
    hasSelectedAddressFromLookup: false,
    showShippingAddressWarning: false,
    showBillingAddressFields: false,
    billingSameAsShipping: true,
    showProcessingModal: false,
    isPaymentFormValid: false,
    shouldTriggerPayment: false,
  },
  catParentDetails: {
    isTransitionVisible: false,
  },
  age: {},
};


export const motivationsForSwitch: Record<TMotivationsForSwitch, TMotivationsForSwitch> = {
  SUPPORTS_HEALTH_CONDITIONS: "SUPPORTS_HEALTH_CONDITIONS",
  HELPS_WITH_HUNGER: "HELPS_WITH_HUNGER",
  LOVES_THE_TASTE: "LOVES_THE_TASTE",
  WANTS_THE_BEST: "WANTS_THE_BEST",
};

export const healthConcernsToAddress: Record<THealthConcernsToAddress, THealthConcernsToAddress> = {
  DULL_COAT: "DULL_COAT",
  SMELLY_POOP: "SMELLY_POOP",
  HEALTHY_WEIGHT: "HEALTHY_WEIGHT",
  LOW_ENERGY: "LOW_ENERGY",
  SICK_OR_DIARRHOEA: "SICK_OR_DIARRHOEA",
  OTHER: "OTHER",
};

export const FOOD_TYPES: Record<TCatFoodTypeBefore, TCatFoodTypeBefore> = {
  DRY: "DRY",
  FRESH: "FRESH",
  WET: "WET",
  RAW: "RAW",
  HOME_COOKED: "HOME_COOKED",
};

export enum SignupFlowSlug {
  // Cat pages
  CAT_COUNT = "/sign-up/fresh",
  BASIC_INFORMATION = "/sign-up/fresh/basic-information",
  AGE = "/sign-up/fresh/age",
  EATING_HABITS = "/sign-up/fresh/eating-habits",
  MOTIVATION = "/sign-up/fresh/motivation",
  FITNESS = "/sign-up/fresh/fitness",
  WEIGHT = "/sign-up/fresh/weight",
  CURRENT_DIET = "/sign-up/fresh/current-diet",
  MEDICAL_CONDITIONS = "/sign-up/fresh/medical-conditions",
  ALLERGIES = "/sign-up/fresh/allergies",

  // User pages
  CAT_PARENT_DETAILS = "/sign-up/fresh/cat-parent-details",
  CHECKOUT = "/sign-up/fresh/checkout",
  PRICING = "/sign-up/fresh/pricing",

  // Pages with no validation
  RECIPES = "/sign-up/fresh/recipes",
  WHY_FRESH = "/sign-up/fresh/why-fresh",
  TREATS = "/sign-up/fresh/treats",
}

export interface ISignupFlowPageMetadata {
  title?: string
  hideProgressSteps?: boolean
  hideFooter?: boolean
  headerBgColor?: Theme
  bgColor?: Theme
  progressBarBgColor?: Theme
  isUserPage?: boolean
  isTransitionScreen?: boolean
  copyCatAnswers?: boolean
  hidePromoBanner?: boolean
  promoBannerAnimateOnEntry?: boolean
  /**
 * Access requirements are used to determine if a page should be displayed to the user.
 * Example A/B test requirement
 * accessRequirements: [
      (context: FlowNavigationContext) => context.inABTestGroup("new-basic-information-flow"),
    ],
 */
  accessRequirements?: PageRequirement[]
}

export const SIGNUP_FLOW_PAGES: Record<SignupFlowSlug, ISignupFlowPageMetadata> = {
  [SignupFlowSlug.CAT_COUNT]: {
    promoBannerAnimateOnEntry: true,
    progressBarBgColor: LIGHT_GREY,
  },
  [SignupFlowSlug.BASIC_INFORMATION]: {
    title: "basicInformation",
  },
  [SignupFlowSlug.AGE]: {
    title: "age",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.EATING_HABITS]: {
    title: "eatingHabits",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MOTIVATION]: {
    title: "motivation",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.FITNESS]: {
    title: "fitness",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.WEIGHT]: {
    title: "weight",
  },
  [SignupFlowSlug.CURRENT_DIET]: {
    title: "currentDiet",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MEDICAL_CONDITIONS]: {
    title: "medicalConditions",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.ALLERGIES]: {
    title: "allergies",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.CAT_PARENT_DETAILS]: {
    hideProgressSteps: true,
    bgColor: LIGHT_GREY,
    headerBgColor: LIGHT_GREY,
    isUserPage: true,
  },
  [SignupFlowSlug.RECIPES]: {
    title: "recipes",
    hideProgressSteps: true,
    hidePromoBanner: true,
  },
  [SignupFlowSlug.WHY_FRESH]: {
    hideProgressSteps: true,
    hidePromoBanner: true,
  },
  [SignupFlowSlug.PRICING]: {
    title: "pricing",
    hideProgressSteps: true,
    isUserPage: true,
    bgColor: "light",
    hidePromoBanner: true,
  },
  [SignupFlowSlug.TREATS]: {
    hideProgressSteps: true,
    isUserPage: true,
    hidePromoBanner: true,
    accessRequirements: [
      (context: FlowNavigationContext) => !context.hasAllAllergies([ "CHICKEN", "SALMON" ]),
    ],
  },
  [SignupFlowSlug.CHECKOUT]: {
    title: "checkout",
    hideProgressSteps: true,
    isUserPage: true,
    bgColor: "light",
    hidePromoBanner: true,
  },
};

export const flowPages: FlowPage<SignupFlowSlug>[] = Object.entries(SIGNUP_FLOW_PAGES).map(
  ([ slug, metadata ]) => ({
    slug: slug as SignupFlowSlug,
    accessRequirements: metadata.accessRequirements ?? [],
    isTransitionScreen: metadata.isTransitionScreen,
  }),
);
